import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Pause, Play, X } from 'lucide-react';
import {
  AnimatePresence,
  motion,
  useIsPresent,
  useReducedMotion,
} from 'motion/react';
import {
  type KeyboardEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import logoWhite from '@/assets/korza_logo_white.png';
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog';
import { useIsMobile } from '@/hooks/use-mobile';
import { useI18nContext } from '@/i18n/i18n-react';
import type {
  EditionModule,
  SlideDefinition,
  SlideProps,
} from '@/lib/whats-new/edition-types';
import {
  createPlayerState,
  playerReducer,
} from '@/lib/whats-new/player-reducer';
import { PlayerNav } from '@/components/whats-new/player/player-nav';
import { ProgressBars } from '@/components/whats-new/player/progress-bars';
import { Stage } from '@/components/whats-new/player/stage';
import { StageTorus } from '@/components/whats-new/player/stage-torus';
import { TapZones } from '@/components/whats-new/player/tap-zones';

export interface EditionPlayerProps {
  edition: EditionModule;
  /** The edition's name: the dialog's accessible name (NEWS-07). */
  name: string;
  /** Header label, e.g. "Estreia · 4 nov" (NEWS-01). */
  label: string;
  /** Slide it opens on (a "Ver" of the archive, NEWS-40). */
  startIndex?: number;
  onClose: () => void;
}

/** A tab in the background pauses rAF; don't skip slides when it returns. */
const MAX_FRAME_MS = 100;

const ICON_BUTTON =
  'relative z-[5] grid size-[34px] place-items-center rounded-full bg-white/[.08] text-white transition-colors hover:bg-white/[.16] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#6FA0FF] max-md:size-11';

const EASE_OUT: [number, number, number, number] = [0.16, 1, 0.3, 1];

/**
 * An edition as stories (NEWS-01…07/09): a navy stage of up to 1080×660 over
 * a blurred scrim on ≥ 768px, the whole screen below. Only the current slide
 * is mounted (plus the one fading out), keyed by visit so its animations and
 * timers restart; under reduced motion nothing advances on its own.
 */
export function EditionPlayer({
  edition,
  name,
  label,
  startIndex = 0,
  onClose,
}: EditionPlayerProps) {
  const { LL } = useI18nContext();
  const reduceMotion = useReducedMotion() ?? false;
  const isMobile = useIsMobile();
  const [state, dispatch] = useReducer(playerReducer, undefined, () =>
    createPlayerState(
      edition.slides.map((slide) => slide.durationMs),
      startIndex
    )
  );
  const closeRef = useRef<HTMLButtonElement>(null);
  const total = edition.slides.length;
  const slide = edition.slides[state.index];

  // Drives the timeline (NEWS-03); never under reduced motion (NEWS-09).
  useEffect(() => {
    if (reduceMotion) return;
    let frame = 0;
    let last = performance.now();
    const loop = (now: number) => {
      dispatch({ type: 'tick', dtMs: Math.min(now - last, MAX_FRAME_MS) });
      last = now;
      frame = requestAnimationFrame(loop);
    };
    frame = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frame);
  }, [reduceMotion]);

  const next = useCallback(() => dispatch({ type: 'next' }), []);
  const prev = useCallback(() => dispatch({ type: 'prev' }), []);
  const goTo = useCallback(
    (index: number) => dispatch({ type: 'go', index }),
    []
  );
  const togglePause = useCallback(() => dispatch({ type: 'togglePause' }), []);
  const hold = useCallback(
    (held: boolean) => dispatch({ type: 'hold', held }),
    []
  );

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowRight') next();
    else if (event.key === 'ArrowLeft') prev();
    else if (event.key === ' ') {
      // On a button, Space presses it (NEWS-05: "fora de um botão").
      const target = event.target;
      if (target instanceof Element && target.closest('button, a')) return;
      event.preventDefault();
      togglePause();
    }
  };

  const slideProps = useMemo<SlideProps>(
    () => ({ next, goTo, close: onClose }),
    [next, goTo, onClose]
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogPortal>
        {/* Scrim and stage sit above the whole shell, help FAB included
            (z-[2147482646], see help-fab.tsx). */}
        <DialogOverlay className="z-[2147482647] bg-[rgba(6,12,30,0.58)] backdrop-blur-[10px] backdrop-saturate-[1.1]" />
        <DialogPrimitive.Content
          asChild
          aria-modal="true"
          aria-label={name}
          aria-describedby={undefined}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            closeRef.current?.focus();
          }}
          // The provider gives the focus back (NEWS-06).
          onCloseAutoFocus={(event) => event.preventDefault()}
          onKeyDown={onKeyDown}
        >
          <motion.div
            data-testid="edition-player"
            initial={
              reduceMotion
                ? false
                : isMobile
                  ? { opacity: 0, y: 40 }
                  : { opacity: 0, y: 28, scale: 0.965 }
            }
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, ease: EASE_OUT }}
            className="fixed inset-0 z-[2147482647] m-auto flex h-[min(660px,calc(100%-48px))] w-[min(1080px,calc(100%-48px))] flex-col overflow-hidden rounded-[22px] bg-[#15191E] shadow-[0_40px_120px_-30px_rgba(0,0,0,0.7),0_0_0_1px_rgba(255,255,255,0.08)] outline-none max-md:h-full max-md:w-full max-md:rounded-none max-md:shadow-none [@media(min-width:768px)_and_(max-height:800px)]:h-[min(660px,calc(100%-28px))]"
          >
            <DialogTitle className="sr-only">{name}</DialogTitle>
            <Stage
              backdrop={
                <StageTorus spot={slide.torusSpot} visit={state.visit} />
              }
            >
              <div className="relative z-[4] flex flex-col gap-3.5 px-[22px] pt-4 max-md:px-[18px] max-md:pt-[max(14px,env(safe-area-inset-top))] [@media(min-width:768px)_and_(max-height:800px)]:gap-2.5 [@media(min-width:768px)_and_(max-height:800px)]:pt-3.5">
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 -bottom-7 top-0 -z-10 bg-[linear-gradient(rgba(9,30,90,0.55),rgba(9,30,90,0))]"
                />
                <ProgressBars state={state} />
                <div className="flex items-center gap-3.5">
                  <img
                    src={logoWhite}
                    alt="Korza"
                    className="h-[15px] w-auto"
                  />
                  <span className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-white/[.86] max-md:hidden">
                    {label}
                  </span>
                  <span className="flex-1" />
                  <button
                    type="button"
                    onClick={togglePause}
                    aria-label={
                      state.paused
                        ? LL.whatsNew.player.resume()
                        : LL.whatsNew.player.pause()
                    }
                    className={ICON_BUTTON}
                  >
                    {state.paused ? (
                      <Play
                        aria-hidden="true"
                        className="size-4"
                        strokeWidth={1.6}
                      />
                    ) : (
                      <Pause
                        aria-hidden="true"
                        className="size-4"
                        strokeWidth={1.6}
                      />
                    )}
                  </button>
                  <button
                    ref={closeRef}
                    type="button"
                    onClick={onClose}
                    aria-label={LL.whatsNew.player.close()}
                    className={ICON_BUTTON}
                  >
                    <X
                      aria-hidden="true"
                      className="size-4"
                      strokeWidth={1.6}
                    />
                  </button>
                </div>
              </div>
              <div className="relative min-h-0 flex-1">
                <TapZones
                  enabled={isMobile}
                  onPrev={prev}
                  onNext={next}
                  onHold={hold}
                >
                  <SlideArea
                    visit={state.visit}
                    slide={slide}
                    label={LL.whatsNew.player.counter({
                      current: state.index + 1,
                      total,
                    })}
                    reduceMotion={reduceMotion}
                    slideProps={slideProps}
                  />
                </TapZones>
              </div>
              <PlayerNav
                index={state.index}
                total={total}
                onPrev={prev}
                onNext={next}
              />
            </Stage>
          </motion.div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

interface SlideFrameProps {
  slide: SlideDefinition;
  label: string;
  reduceMotion: boolean;
  slideProps: SlideProps;
}

/**
 * The current slide and the one fading out. Memoized: the timeline
 * re-renders the player every frame, the slides only when they change.
 */
const SlideArea = memo(function SlideArea({
  visit,
  ...frame
}: SlideFrameProps & { visit: number }) {
  return (
    <AnimatePresence initial={false}>
      <SlideFrame key={visit} {...frame} />
    </AnimatePresence>
  );
});

/** One visit of a slide; the one fading out is inert. */
function SlideFrame({
  slide,
  label,
  reduceMotion,
  slideProps,
}: SlideFrameProps) {
  const isPresent = useIsPresent();
  const { Component } = slide;
  return (
    <motion.div
      role="group"
      aria-roledescription="slide"
      aria-label={label}
      aria-hidden={isPresent ? undefined : true}
      inert={!isPresent}
      data-slide-id={slide.id}
      className="absolute inset-0"
      initial={{ opacity: 0 }}
      animate={{
        opacity: 1,
        transition: reduceMotion
          ? { duration: 0 }
          : { duration: 0.5, delay: 0.1, ease: 'easeOut' },
      }}
      exit={{
        opacity: 0,
        transition: reduceMotion
          ? { duration: 0 }
          : { duration: 0.45, ease: 'easeOut' },
      }}
    >
      <Component {...slideProps} />
    </motion.div>
  );
}
